import { useCallback } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { SNACKBAR_SKIN } from 'components/shared/Snackbar';
import { GENERAL_ERROR_MESSAGE } from 'constants/texts';
import { showSnackbar } from 'hooks/useSnackbar';
import { setGlobalLoader, setOwnershipAction } from 'store/admin/files';
import { loadAssetsListThunk } from 'store/admin/files/thunks';
import { getSearchFiltersThunk } from 'store/admin/pagesTable/thunks';
import { accountIdSelector } from 'store/user/selectors';
import { confirmAlert } from 'utils/alert';
import axiosApi from 'utils/axios';

const mapping = {
  all: 'all',
  sp: 'portal',
  my: 'private',
};

export const useMediaFilter = ({ filter, filterName, filterKeyName, onCloseModal }) => {
  const dispatch = useDispatch();
  const accountId = useSelector(accountIdSelector);

  const defaultFilter = getDefaultFilter();
  const restoredFilter = filter || getDefaultFilter(filter);

  const handleDelete = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();

      confirmAlert({
        title: 'Are you sure you want to delete this filter?',
        onConfirm: async () => {
          try {
            dispatch(setGlobalLoader(true));
            await axiosApi.delete(`/acc/${accountId}/search_filters/${filter.id}`);
            await dispatch(getSearchFiltersThunk('media_library'));
            dispatch(setOwnershipAction('all'));
            dispatch(loadAssetsListThunk('media', true, true, true));
          } catch (err) {
            showSnackbar({ text: GENERAL_ERROR_MESSAGE, type: SNACKBAR_SKIN.ERROR });
          } finally {
            dispatch(setGlobalLoader(false));
          }
        },
      });
    },
    [dispatch, filter],
  );

  const handleSave = useCallback(
    async (newFilter) => {
      try {
        dispatch(setGlobalLoader(true));
        const method = newFilter.id ? 'patch' : 'post';
        const tail = newFilter.id ? `/${newFilter.id}` : '';
        await axiosApi[method](`/acc/${accountId}/search_filters${tail}`, {
          dashboard: 'media_library',
          table: 'media',
          ...newFilter,
        });
        await dispatch(getSearchFiltersThunk('media_library'));
        dispatch(setOwnershipAction(mapping[newFilter.data.show]));
        dispatch(loadAssetsListThunk('media', true, true, true));
        onCloseModal && onCloseModal();
        showSnackbar({ text: 'Filters have been saved.' });
      } catch (e) {
        showSnackbar({ text: GENERAL_ERROR_MESSAGE, type: SNACKBAR_SKIN.ERROR });
      } finally {
        setGlobalLoader(false);
      }
    },
    [filterKeyName, dispatch],
  );

  return {
    title: `Filter ${filterName}`,
    defaultFilter,
    restoredFilter,
    handleDelete,
    handleSave,
  };
};

const getDefaultFilter = (filter = {}) => {
  return {
    ...filter,
    data: {
      show: 'all',
      tags: [],
    },
  };
};
